import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useState } from "react";

interface BlogCardProps {
  post: {
    id: string;
    title: string;
    slug: string;
    excerpt: string | null;
    content?: string | null;
    created_at: string;
  };
  index: number;
}

const getExcerpt = (post: BlogCardProps['post']) => {
  if (post.excerpt) return post.excerpt;
  if (post.content) {
    const plainText = post.content.replace(/<[^>]*>/g, '');
    return plainText.slice(0, 150) + '...';
  }
  return 'Kein Auszug verfügbar';
};

export const BlogCard = ({ post, index }: BlogCardProps) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  // Mouse position tracking for 3D tilt
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Spring animations for smooth movement
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [5, -5]), {
    stiffness: 150,
    damping: 30
  });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-5, 5]), {
    stiffness: 150,
    damping: 30
  });

  // Dynamic shadow
  const shadowX = useSpring(useTransform(mouseX, [-0.5, 0.5], [-10, 10]), {
    stiffness: 150,
    damping: 30
  });
  const shadowY = useSpring(useTransform(mouseY, [-0.5, 0.5], [-10, 10]), {
    stiffness: 150,
    damping: 30
  });

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    if (window.innerWidth < 768) return; // Disable on mobile

    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    
    mouseX.set(x);
    mouseY.set(y);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
    setIsHovered(false);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ 
        delay: index * 0.1, 
        duration: 0.5,
        ease: "easeOut"
      }}
      style={{
        rotateX,
        rotateY,
        transformStyle: "preserve-3d",
        willChange: "transform"
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      onClick={() => navigate(`/blog/${post.slug}`)}
      className="cursor-pointer group relative h-full"
    >
      {/* Pulsing Border Glow */}
      <motion.div
        className="absolute -inset-[1px] rounded-[33px] opacity-0 group-hover:opacity-25 pointer-events-none transition-opacity duration-500"
        style={{
          background: "linear-gradient(90deg, hsl(var(--accent)), hsl(var(--primary)), hsl(var(--accent)))",
          backgroundSize: "200% 100%",
          filter: "blur(8px)"
        }}
        animate={isHovered ? {
          backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
        } : {}}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "linear"
        }}
      />

      {/* Dynamic Shadow */}
      <motion.div
        className="absolute inset-0 rounded-[33px] opacity-0 group-hover:opacity-15 pointer-events-none transition-opacity duration-300"
        style={{
          x: shadowX,
          y: shadowY,
          background: "radial-gradient(circle at center, hsl(var(--accent) / 0.4), transparent 70%)",
          filter: "blur(20px)"
        }}
      />

      <Card 
        className="h-full flex flex-col overflow-hidden group
          backdrop-blur-xl bg-white/[0.115] 
          border-2 border-accent/40
          rounded-[33px]
          shadow-[0_8px_32px_rgba(0,0,0,0.12)]
          hover:shadow-[0_20px_60px_rgba(139,195,74,0.15)]
          relative
          after:absolute after:inset-0 
          after:bg-gradient-to-br after:from-accent/[0.03] after:to-transparent
          after:opacity-0 after:group-hover:opacity-100 
          after:pointer-events-none
          after:rounded-[33px]
          transition-all duration-300 ease-out
          [&>*]:relative [&>*]:z-10
          will-change-transform"
        style={{
          transform: "translateZ(15px)"
        }}
      >
        <motion.div
          whileHover={{ scale: window.innerWidth >= 768 ? 1.01 : 1.005 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="h-full flex flex-col"
        >
          <CardHeader className="pb-1 sm:pb-2 px-4 sm:px-5 lg:px-6 pt-4 sm:pt-5 lg:pt-6">
            {/* Datum */}
            <div className="text-xs sm:text-sm text-slate-300/90 mb-2 sm:mb-3 font-medium">
              {new Date(post.created_at).toLocaleDateString('de-DE', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
            
            {/* Titel mit automatischem Umbruch */}
            <CardTitle lang="de" className="text-lg sm:text-xl font-bold leading-tight mb-1 sm:mb-2 text-white group-hover:text-accent transition-colors line-clamp-2 break-words hyphens-auto lg:text-sm py-0 px-0 my-0 text-left">
              {post.title}
            </CardTitle>
          </CardHeader>
          
          <CardContent className="flex-1 flex flex-col pt-0 px-4 sm:px-5 lg:px-6 pb-4 sm:pb-5 lg:pb-6">
            {/* Excerpt */}
            <p lang="de" className="text-xs sm:text-sm text-slate-400/80 mb-4 sm:mb-6 flex-1 leading-relaxed line-clamp-3 break-words hyphens-auto mt-0 text-left">
              {getExcerpt(post)}
            </p>
            
            {/* CTA Button */}
            <Button 
              variant="outline"
              className="w-full sm:w-auto
                bg-transparent backdrop-blur-sm
                border-2 border-accent/60
                text-white font-semibold text-xs sm:text-sm
                hover:bg-accent/20 hover:border-accent
                transition-all duration-300
                rounded-full px-4 sm:px-6 py-2
                shadow-[0_0_20px_rgba(139,195,74,0.0)]
                hover:shadow-[0_0_20px_rgba(139,195,74,0.3)]
                group-hover:scale-105"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/blog/${post.slug}`);
              }}
            >
              Artikel lesen →
            </Button>
          </CardContent>
        </motion.div>
      </Card>

      {/* Corner Accent Glows */}
      <div 
        className="absolute top-0 right-0 w-32 h-32 bg-accent/0 group-hover:bg-accent/8 rounded-full blur-3xl pointer-events-none transition-all duration-700 opacity-0 group-hover:opacity-100"
        style={{ transform: "translate(50%, -50%)" }}
      />
      <div 
        className="absolute bottom-0 left-0 w-32 h-32 bg-primary/0 group-hover:bg-primary/8 rounded-full blur-3xl pointer-events-none transition-all duration-700 opacity-0 group-hover:opacity-100"
        style={{ transform: "translate(-50%, 50%)" }}
      />
    </motion.article>
  );
};
